import React, { useState, useEffect } from "react";
import { Form, Input, Button, message, Table, Popconfirm } from "antd";
import { useNavigate } from "react-router-dom";
import { CustomListService } from "../../Services/CustomListService";
import { WebApiCustomListsDtoCreateCustomListDto, WebApiCustomListsDtoCustomListDto } from "../../Repository/eventbookyapi";
import LayoutLeftMenu from "../../Components/Layout/LayoutLeftMenu";
import LeftMenuSettings from "./LeftSettings";
import { TextService } from "../../Services/TextService";
import Title from "antd/es/typography/Title";
import { getCurrentLanguage } from "../../Helpers/RouteHelper";
import { SubscriptionLevel } from "../../Types/SubscriptionLevel";

const CustomListSettings: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [lists, setLists] = useState<WebApiCustomListsDtoCustomListDto[]>([]);
    const [tableLoading, setTableLoading] = useState(false);
    const navigate = useNavigate();
    const GetText = TextService.GetText;

    const fetchLists = async () => {
        setTableLoading(true);
        try {
            const data = await CustomListService.GetCustomLists();
            setLists(data);
        } catch {
            message.error("Kunde inte hämta listor.");
        }
        setTableLoading(false);
    };

    const handleDelete = async (id: number) => {
        setTableLoading(true);
        try {
            await CustomListService.DeleteCustomList(id);
            message.success("Listan har tagits bort!");
            fetchLists();
        } catch {
            message.error("Kunde inte ta bort listan.");
        }
        setTableLoading(false);
    };

    useEffect(() => {
        fetchLists();
    }, []);

    const onFinish = async (values: WebApiCustomListsDtoCreateCustomListDto) => {
        setLoading(true);
        try {
            await CustomListService.CreateCustomList(values);
            message.success("Listan har skapats!");
            fetchLists();
        } catch (error) {
            message.error("Kunde inte skapa listan.");
        }
        setLoading(false);
    };

    const columns = [
        {
            title: "Namn",
            dataIndex: "name",
            key: "name",
        },
        {
            title: "Beskrivning",
            dataIndex: "description",
            key: "description",
        },
        {
            title: "Hantera",
            key: "action",
            render: (_: any, record: WebApiCustomListsDtoCustomListDto) => (
                <>
                    <Button
                        type="primary"
                        onClick={() => navigate('/' + getCurrentLanguage() + `/editCustomList/${record.id}`)}
                        size="small"
                        style={{ marginRight: 8 }}
                    >
                        Öppna
                    </Button>
                    <Popconfirm
                        title="Är du säker på att du vill ta bort listan?"
                        onConfirm={() => handleDelete(record.id!)}
                        okText="Ja"
                        cancelText="Nej"
                    >
                        <Button danger size="small">
                            Ta bort
                        </Button>
                    </Popconfirm>
                </>
            ),
        },
    ];

    return (
        <LayoutLeftMenu menu={<LeftMenuSettings selected={"customListsSettings"} />}>
            <Title level={2}>{GetText("CustomListSettings_Title")}</Title>
            <div style={{ marginBottom: 24, fontSize: 16 }}>
                {GetText("CustomList_Description")}
            </div>
            <Form layout="vertical" onFinish={onFinish}>
                <Form.Item
                    label="Listnamn"
                    name="name"
                    rules={[{ required: true, message: "Ange ett namn för listan" }]}
                >
                    <Input placeholder="Namn på lista" />
                </Form.Item>
                <Form.Item
                    label="Beskrivning"
                    name="description"
                    rules={[{ required: false }]}
                >
                    <Input.TextArea placeholder="Beskrivning (valfritt)" />
                </Form.Item>
                <Form.Item>
                    <Button disabled={user!.subscriptionLevel < SubscriptionLevel.Premium} type="primary" htmlType="submit" loading={loading}>
                        Skapa lista
                    </Button>
                    {user!.subscriptionLevel < SubscriptionLevel.Enterprise && <span> Uppgradera <a href='/Pricing'>till enterprise</a> för att skapa egna datatabeller</span>}
                </Form.Item>
            </Form>
            <Table
                style={{ marginTop: 32 }}
                columns={columns}
                dataSource={lists}
                rowKey="id"
                loading={tableLoading}
                pagination={false}
            />
        </LayoutLeftMenu>
    );
};

export default CustomListSettings;